<style lang="postcss">
	.phone-field-input {
		display: flex;
		flex-direction: row;
		align-items: center;
		white-space: nowrap;
	}
	.selected-country-cover {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.25rem;
	}
	.selected-country-cover-inner {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 0.25rem;
	}
	.phone-input {
		appearance: none;
		display: flex;
		flex: 1;
		min-width: 0;
		margin-left: 0.5rem;
		background-color: transparent;
	}
	.phone-input:focus {
		outline: none;
	}
</style>

<script lang="ts">
	import FieldError from "$form/Fields/Shared/FieldError.svelte";
	import Label from "$form/Fields/Shared/Label.svelte";
	import Description from "$form/Fields/Shared/Description.svelte";
	import {
		AsYouType,
		parsePhoneNumber,
		type CountryCode
	} from "libphonenumber-js/min";
	import { createEventDispatcher, onMount, getContext, tick } from "svelte";
	import { BROWSER } from "esm-env";

	export let field: FieldDisplay & { type: "Phone" };
	export let value: string;

	let inputRef: HTMLInputElement;
	let inputCoverRef: HTMLLabelElement;
	let selectedCountry: string = getContext<string>("countryCode") ?? "US";
	const styling = getContext<Styling>("styling");
	let elementWidth: number = 0;
	let internalValue: string = "";
	let fontSize: string = "16px";

	const dispatch = createEventDispatcher();
	let countriesOptions: ChoiceOption[] = [];
	let SelectComponent: any;

	async function updateValue(event: Event) {
		const inputValue = (event.target as HTMLInputElement).value ?? "";
		const asYouType = new AsYouType({
			defaultCountry: (selectedCountry ?? "US") as CountryCode
		});
		internalValue = asYouType.input(inputValue);
		if (asYouType.isValid() && asYouType.getNumber()?.number) {
			value = asYouType.getNumber()?.number as string;
		} else {
			value = inputValue;
		}
	}

	const getFlagEmoji = (countryCode: string) => {
		const country = countriesOptions.find((x) => x.value === countryCode);
		return country?.embed?.content ?? "";
	};

	const getCode = (countryCode: string) => {
		const country = countriesOptions.find((x) => x.value === countryCode);
		return country?.meta?.pill ?? "";
	};

	onMount(async () => {
		if (BROWSER) {
			SelectComponent = await import("$basic/Select");
			const getPhoneCountries = (await import("$lib/data/phoneCountries"))
				.getPhoneCountries;
			countriesOptions = getPhoneCountries();
			if (value) {
				let parsed;
				try {
					parsed = parsePhoneNumber(value, selectedCountry as CountryCode);
				} catch (e) {}
				if (parsed) {
					selectedCountry = parsed.country as string;
					internalValue = parsed.nationalNumber;
					await tick();
					if (inputRef) {
						inputRef.dispatchEvent(new Event("input", { bubbles: true }));
					}
				}
			}
			if (inputRef) fontSize = getComputedStyle(inputRef).fontSize;
		}
		if (inputRef) {
			inputRef.addEventListener("focus", () => {
				dispatch("updateOther", {
					active: true
				});
			});
			inputRef.addEventListener("blur", () => {
				dispatch("updateOther", {
					tainted: true
				});
				dispatch("updateOther", {
					active: false
				});
			});
		}
	});
</script>

<div
	class="field-label-cover"
	class:has-placeholder="{styling.placeholders !== false}">
	{#if field.labelHTMLComputed && field.settings.hideLabel !== true}
		{#if styling.placeholders === false}
			<Label html="{field.labelHTMLComputed}" />
		{/if}
		{#if field.descriptionHTMLComputed}
			<Description html="{field.descriptionHTMLComputed}" />
		{/if}
	{/if}
</div>
<div class="field-input-cover" bind:clientWidth="{elementWidth}">
	<label
		bind:this="{inputCoverRef}"
		for="phone-{field.id}"
		class="phone-field-input field-input">
		{#if SelectComponent}
			{#key selectedCountry}
				<svelte:component
					this="{SelectComponent.Select}"
					tabindex="{-1}"
					uncheckOnClick="{false}"
					fancyPlacement="{false}"
					truncate="{true}"
					class="text-sm"
					options="{countriesOptions}"
					cross="{false}"
					bind:first="{selectedCountry}"
					fixedWidth="{elementWidth}"
					positionRef="{inputCoverRef}"
					returnValue="{true}"
					on:update="{() => {
						inputRef.focus();
					}}">
					<div class="selected-country-cover" slot="selectedItem">
						<span class="selected-country-cover-inner">
							{#if selectedCountry && countriesOptions.length > 0}
								<span style="font-size: 1.5rem; line-height: 1"
									>{getFlagEmoji(selectedCountry)}</span>
								<span style="font-size: {fontSize}; opacity: 0.5"
									>{getCode(selectedCountry)}</span>
							{:else}
								...
							{/if}
						</span>
					</div>
				</svelte:component>
			{/key}
		{/if}
		<input
			type="tel"
			on:input="{updateValue}"
			bind:this="{inputRef}"
			id="phone-{field.id}"
			name="{field.id}"
			inputmode="tel"
			aria-label="{field.labelComputed || ''}"
			placeholder="{styling.placeholders ? field.labelComputed : ''}"
			class="phone-input"
			value="{internalValue ?? ''}" />
	</label>
	<FieldError
		visible="{field.tainted}"
		valid="{field.valid}"
		errorNumber="{field.errorNumber}"
		errorType="{field.errorType}" />
</div>
